import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth } from '../../../../frontend/src/firebase';
import {
  BaseDialog,
  SAccordion,
} from '@modules/ui-components';

interface AuthStatus {
  loading: boolean;
  authenticated: boolean;
  user: {
    uid: string;
    email: string | null;
  } | null;
  error: string | null;
}

interface AutoLoginProps {
  children?: React.ReactNode;
}

/**
 * AutoLogin Component
 *
 * Automatically authenticates using test credentials from environment variables.
 * Used for E2E testing to bypass manual login flow.
 *
 * Environment Variables Required:
 * - VITE_E2E_TEST_EMAIL: Test user email
 * - VITE_E2E_TEST_PASSWORD: Test user password
 */
export const AutoLogin: React.FC<AutoLoginProps> = ({ children }) => {
  const theme = useTheme();
  const [authStatus, setAuthStatus] = useState<AuthStatus>({
    loading: true,
    authenticated: false,
    user: null,
    error: null,
  });

  const performLogin = async () => {
    setAuthStatus({
      loading: true,
      authenticated: false,
      user: null,
      error: null,
    });

    try {
      // Load credentials from environment variables
      const email = import.meta.env.VITE_E2E_TEST_EMAIL;
      const password = import.meta.env.VITE_E2E_TEST_PASSWORD;

      if (!email || !password) {
        throw new Error(
          'Missing test credentials. Please set VITE_E2E_TEST_EMAIL and VITE_E2E_TEST_PASSWORD in .env.dev'
        );
      }

      // Sign in with email and password
      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      setAuthStatus({
        loading: false,
        authenticated: true,
        user: {
          uid: userCredential.user.uid,
          email: userCredential.user.email,
        },
        error: null,
      });
    } catch (error: any) {
      setAuthStatus({
        loading: false,
        authenticated: false,
        user: null,
        error: error.message || 'Authentication failed',
      });
    }
  };

  // Auto-login on mount
  useEffect(() => {
    performLogin();
  }, []);

  // CRITICAL: Don't render children until authentication is complete
  // This prevents race condition where children make Firestore requests before auth
  // (would fail with CORS error due to Security Rules requiring request.auth != null)

  // If still loading, show loading state (don't render children yet)
  if (authStatus.loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          backgroundColor: theme.palette.background.default,
          padding: theme.spacing(2),
        }}
      >
        <Box sx={{ width: '100%' }}>
          <BaseDialog mode="embedded" open={true} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  mb: 3,
                  pb: 2,
                  borderBottom: '1px solid',
                  borderColor: theme.palette.divider,
                }}
              >
                <Typography variant="h5" color="text.primary">
                  Test Harness - Auto Login
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Development/Testing Only
                </Typography>
              </Box>

              <SAccordion title="Authentication">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body1" color="text.secondary">
                    Authenticating with test credentials...
                  </Typography>
                </Box>
              </SAccordion>
            </Box>
          </BaseDialog>
        </Box>
      </Box>
    );
  }

  // If authenticated, render children (ONLY after successful auth)
  if (authStatus.authenticated && children) {
    return <div data-auth-status="authenticated">{children}</div>;
  }

  // Otherwise, show auth error
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(2),
      }}
    >
      <Box sx={{ width: '100%' }}>
        <BaseDialog mode="embedded" open={true} sx={{ maxWidth: 900, mx: 'auto', width: '100%' }}>
          <Box sx={{ p: 3 }}>
            {/* Custom header */}
            <Box
              sx={{
                mb: 3,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: theme.palette.divider,
              }}
            >
              <Typography variant="h5" color="text.primary">
                Test Harness - Auto Login
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Development/Testing Only
              </Typography>
            </Box>

            {/* Authentication sections */}
            {authStatus.loading && (
              <SAccordion title="Authentication">
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: theme.spacing(2),
                  }}
                >
                  <CircularProgress />
                  <Typography variant="body1" color="text.secondary">
                    Authenticating with test credentials...
                  </Typography>
                </Box>
              </SAccordion>
            )}

            {authStatus.error && (
              <SAccordion title="Authentication Error">
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.palette.text.secondary,
                    marginBottom: theme.spacing(3),
                    fontFamily: 'monospace',
                    backgroundColor: theme.palette.background.paper,
                    padding: theme.spacing(2),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.error.main}`,
                  }}
                >
                  {authStatus.error}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={performLogin}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  Retry Login
                </Button>
              </SAccordion>
            )}

            {authStatus.authenticated && authStatus.user && (
              <SAccordion title="Authentication Successful">
                <Box
                  sx={{
                    backgroundColor: theme.palette.background.paper,
                    padding: theme.spacing(2),
                    borderRadius: theme.shape.borderRadius,
                    border: `1px solid ${theme.palette.success.main}`,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.success.main,
                      marginBottom: theme.spacing(2),
                      fontWeight: 500,
                    }}
                  >
                    ✓ Authentication Successful
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      marginBottom: theme.spacing(1),
                    }}
                  >
                    <strong>User ID:</strong> {authStatus.user.uid}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                    }}
                  >
                    <strong>Email:</strong> {authStatus.user.email}
                  </Typography>
                </Box>
              </SAccordion>
            )}
          </Box>
        </BaseDialog>
      </Box>
    </Box>
  );
};

export default AutoLogin;
